/**
 * src/components/ui/Button.jsx — the canonical button.
 *
 * Wraps the existing `.btn`/`.btn-primary`/`.btn-danger`/`.btn-sm` classes
 * (styles.css) rather than inventing a parallel set — those classes already
 * carry Stencil's tokens and the existing press-not-lift motion. This
 * component's job is consistent states (loading, disabled) and a real
 * `aria-busy`/spinner pattern, which the raw `.btn` class alone doesn't
 * give every call site for free.
 */

/**
 * @param {{
 *   variant?: 'primary'|'secondary'|'danger'|'ghost',
 *   size?: 'md'|'sm',
 *   loading?: boolean,
 *   children: React.ReactNode,
 * } & React.ButtonHTMLAttributes<HTMLButtonElement>} props
 * @returns {JSX.Element}
 */
export function Button({ variant = 'secondary', size = 'md', loading = false, disabled, children, className, ...rest }) {
  const classes = [
    'btn',
    variant === 'primary' ? 'btn-primary' : null,
    variant === 'danger' ? 'btn-danger' : null,
    variant === 'ghost' ? 'btn-ghost' : null,
    size === 'sm' ? 'btn-sm' : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button type="button" className={classes} disabled={disabled || loading} aria-busy={loading || undefined} {...rest}>
      {loading ? (
        <>
          <span className="ui-spinner" aria-hidden="true" />
          <span className="ui-visually-hidden">Loading</span>
        </>
      ) : null}
      {children}
    </button>
  );
}
